import { useState } from "react";
import { ShieldAlert } from "lucide-react";
import api, { errMsg } from "../lib/api";
import { useAuth } from "../lib/auth";
import { toast } from "../lib/notices";

/**
 * Persistent warning shown while an admin is acting as another user.
 *
 * Sticky and full-width on purpose. An impersonated session is otherwise
 * indistinguishable from the customer's own, and the failure mode is an
 * operator forgetting which account they are in and changing real settings —
 * or worse, deleting something — believing it is their own.
 */
export default function ImpersonationBanner() {
  const { user, refresh } = useAuth();
  const [stopping, setStopping] = useState(false);

  if (!user?.impersonated_by) return null;

  const stop = async () => {
    setStopping(true);
    try {
      const r = await api.post("/auth/stop-impersonation");
      if (r.data?.restored) {
        toast.success("Returned to your own account");
      } else {
        toast.warning("Impersonation ended, but your own session had expired — please sign in again.");
      }
      await refresh?.();
      window.location.assign(r.data?.restored ? "/admin" : "/login");
    } catch (e) {
      toast.error(errMsg(e, "Could not end the impersonation"));
      setStopping(false);
    }
  };

  return (
    <div
      role="alert"
      className="sticky top-0 z-modal bg-brand text-ink-on-accent brutal-border border-l-0 border-r-0 border-t-0"
      data-testid="impersonation-banner"
    >
      <div className="max-w-wide mx-auto px-6 md:px-10 py-2 flex flex-wrap items-center justify-between gap-3">
        <p className="flex items-center gap-2 text-sm font-mono-ui">
          <ShieldAlert size={16} aria-hidden="true" />
          <span>
            Viewing as <strong>{user.email}</strong> — signed in by {user.impersonated_by}.
            Anything you do here happens to their account.
          </span>
        </p>
        <button
          type="button"
          onClick={stop}
          disabled={stopping}
          className="brutal-btn brutal-btn-sm bg-canvas text-ink"
          data-testid="stop-impersonation"
        >
          {stopping ? "Ending…" : "Stop impersonating"}
        </button>
      </div>
    </div>
  );
}
